import React from "react";

import cloneDeep from "lodash/cloneDeep";

import Table from "@cloudscape-design/components/table";
import Pagination from "@cloudscape-design/components/pagination";
import Header from "@cloudscape-design/components/header";
import Input from "@cloudscape-design/components/input";
import Select from "@cloudscape-design/components/select";
import Button from "@cloudscape-design/components/button";
import Grid from "@cloudscape-design/components/grid";
import Box from "@cloudscape-design/components/box";
import Link from "@cloudscape-design/components/link";
import Badge from "@cloudscape-design/components/badge";
import Preferences from "@cloudscape-design/components/collection-preferences";
import Alert from "@cloudscape-design/components/alert";
import Modal from "@cloudscape-design/components/modal";
import FormField from "@cloudscape-design/components/form-field";

import Avatar from "react-avatar";

/* Recoil */
import { useRecoilValue, useSetRecoilState } from "recoil";
import { configStore } from "../stores/config";
import { playerStore } from "../stores/players";
import { userStore } from "../stores/access";
import { playerPageStore } from "../stores/playerPage";
import { SpaceBetween } from "@cloudscape-design/components";
/* ------- */

let isLoading = false;
const EXPORT_FORMAT_OPTIONS = [
	{ value: "json", label: "JSON" },
	{ value: "csv", label: "CSV" },
	{ value: "xlsx", label: "XLSX" },
];

function getExportOption(format) {
	return EXPORT_FORMAT_OPTIONS.find((option) => option.value === format) || EXPORT_FORMAT_OPTIONS[0];
}

function flattenExportValue(value) {
	if (value === null || typeof value === "undefined") return "";
	if (typeof value === "object") return JSON.stringify(value);
	return value;
}

function getExportColumns(data) {
	const columns = [];

	data.forEach((item) => {
		Object.keys(item).forEach((key) => {
			if (!columns.includes(key)) columns.push(key);
		});
	});

	return columns;
}

function escapeCsvValue(value) {
	const stringValue = String(flattenExportValue(value));
	return /[",\n\r]/.test(stringValue) ? `"${stringValue.replace(/"/g, '""')}"` : stringValue;
}

function getCsvContent(data) {
	const columns = getExportColumns(data);
	const rows = data.map((item) => columns.map((column) => escapeCsvValue(item[column])).join(","));

	return [columns.join(","), ...rows].join("\n");
}

function escapeXml(value) {
	return String(flattenExportValue(value))
		.replace(/&/g, "&amp;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;")
		.replace(/"/g, "&quot;")
		.replace(/'/g, "&apos;");
}

function getColumnLetter(index) {
	let column = "";
	let current = index + 1;

	while (current > 0) {
		const remainder = (current - 1) % 26;
		column = String.fromCharCode(65 + remainder) + column;
		current = Math.floor((current - 1) / 26);
	}

	return column;
}

function createCrcTable() {
	const table = [];

	for (let i = 0; i < 256; i++) {
		let value = i;
		for (let j = 0; j < 8; j++) value = value & 1 ? 0xedb88320 ^ (value >>> 1) : value >>> 1;
		table[i] = value >>> 0;
	}

	return table;
}

const CRC_TABLE = createCrcTable();

function crc32(bytes) {
	let crc = 0xffffffff;

	for (let i = 0; i < bytes.length; i++) crc = CRC_TABLE[(crc ^ bytes[i]) & 0xff] ^ (crc >>> 8);

	return (crc ^ 0xffffffff) >>> 0;
}

function uint16(value) {
	return [value & 0xff, (value >>> 8) & 0xff];
}

function uint32(value) {
	return [value & 0xff, (value >>> 8) & 0xff, (value >>> 16) & 0xff, (value >>> 24) & 0xff];
}

function stringBytes(value) {
	return Array.from(new TextEncoder().encode(value));
}

function appendBytes(target, bytes) {
	for (let i = 0; i < bytes.length; i++) target.push(bytes[i]);
}

function createBlobFromZipParts(localParts, centralParts, endParts) {
	const output = new Uint8Array(localParts.length + centralParts.length + endParts.length);
	output.set(localParts, 0);
	output.set(centralParts, localParts.length);
	output.set(endParts, localParts.length + centralParts.length);

	return new Blob([output], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
}

function createZip(files) {
	const localParts = [];
	const centralParts = [];
	let offset = 0;

	files.forEach((file) => {
		const nameBytes = stringBytes(file.name);
		const dataBytes = stringBytes(file.content);
		const crc = crc32(dataBytes);
		const localHeader = [
			...uint32(0x04034b50), ...uint16(20), ...uint16(0), ...uint16(0), ...uint16(0), ...uint16(0),
			...uint32(crc), ...uint32(dataBytes.length), ...uint32(dataBytes.length), ...uint16(nameBytes.length), ...uint16(0), ...nameBytes,
		];

		appendBytes(localParts, localHeader);
		appendBytes(localParts, dataBytes);
		centralParts.push(
			...uint32(0x02014b50), ...uint16(20), ...uint16(20), ...uint16(0), ...uint16(0), ...uint16(0), ...uint16(0),
			...uint32(crc), ...uint32(dataBytes.length), ...uint32(dataBytes.length), ...uint16(nameBytes.length), ...uint16(0), ...uint16(0),
			...uint16(0), ...uint16(0), ...uint32(0), ...uint32(offset), ...nameBytes
		);
		offset += localHeader.length + dataBytes.length;
	});

	const end = [
		...uint32(0x06054b50), ...uint16(0), ...uint16(0), ...uint16(files.length), ...uint16(files.length),
		...uint32(centralParts.length), ...uint32(localParts.length), ...uint16(0),
	];

	return createBlobFromZipParts(localParts, centralParts, end);
}

function getXlsxBlob(data) {
	const columns = getExportColumns(data);
	const rows = [columns, ...data.map((item) => columns.map((column) => flattenExportValue(item[column])))];
	const sheetData = rows.map((row, rowIndex) => {
		const cells = row.map((value, columnIndex) => {
			const cellRef = `${getColumnLetter(columnIndex)}${rowIndex + 1}`;
			return `<c r="${cellRef}" t="inlineStr"><is><t>${escapeXml(value)}</t></is></c>`;
		}).join("");

		return `<row r="${rowIndex + 1}">${cells}</row>`;
	}).join("");

	return createZip([
		{ name: "[Content_Types].xml", content: '<?xml version="1.0" encoding="UTF-8"?><Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="xml" ContentType="application/xml"/><Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/><Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/><Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/></Types>' },
		{ name: "_rels/.rels", content: '<?xml version="1.0" encoding="UTF-8"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/></Relationships>' },
		{ name: "xl/workbook.xml", content: '<?xml version="1.0" encoding="UTF-8"?><workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"><sheets><sheet name="Export" sheetId="1" r:id="rId1"/></sheets></workbook>' },
		{ name: "xl/_rels/workbook.xml.rels", content: '<?xml version="1.0" encoding="UTF-8"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/><Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/></Relationships>' },
		{ name: "xl/styles.xml", content: '<?xml version="1.0" encoding="UTF-8"?><styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><fonts count="1"><font><sz val="11"/><name val="Calibri"/></font></fonts><fills count="1"><fill><patternFill patternType="none"/></fill></fills><borders count="1"><border/></borders><cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs><cellXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/></cellXfs></styleSheet>' },
		{ name: "xl/worksheets/sheet1.xml", content: `<?xml version="1.0" encoding="UTF-8"?><worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><sheetData>${sheetData}</sheetData></worksheet>` },
	]);
}

class Players extends React.PureComponent {
	constructor(props) {
		super(props);
		this.state = {
			exportModalVisible: false,
			exportFormat: "json",
		};
		this.currentSearch = "";
		this.query = {};
		window.location.search
			.substr(1)
			.split("&")
			.forEach((item) => {
				this.query[item.split("=")[0]] = item.split("=")[1];
			});
	}

	setPlayerProfileScrollLock(isLocked) {
		if (typeof document === "undefined") return;

		const className = this.props.styles["playersProfileModalOpen"];

		[document.documentElement, document.body].forEach((element) => {
			if (!element) return;

			Array.from(element.classList)
				.filter((currentClassName) => currentClassName === className || currentClassName.includes("playersProfileModalOpen"))
				.forEach((currentClassName) => element.classList.remove(currentClassName));

			if (isLocked) element.classList.add(className);
		});
	}

	componentDidMount() {
		if (typeof this.query.ip !== "undefined") {
			this.currentSearch = this.query.ip;
			this.props.setPlayers({ ...this.props.players, data: [], loaded: false, currentSearch: this.query.ip, currentSearchType: this.props.config.players.options.type[2].type, currentOption: { ...this.props.players.currentOption, order: this.props.config.players.options.order[0], direction: this.props.config.players.options.direction[0], type: this.props.config.players.options.type[2] } });
			setTimeout(() => {
				if (this.props.players.currentSearch === this.query.ip) this.loadPlayers(true);
			}, 0);
		} else if (typeof this.query.refer !== "undefined") {
			this.currentSearch = this.query.refer;
			this.props.setPlayers({ ...this.props.players, currentSearch: this.query.refer, currentSearchType: this.props.config.players.options.type[3].type, currentOption: { ...this.props.players.currentOption, order: this.props.config.players.options.order[0], direction: this.props.config.players.options.direction[0], type: this.props.config.players.options.type[3] } });
			setTimeout(() => {
				if (this.props.players.currentSearch === this.query.refer) this.loadPlayers(true);
			}, 0);
		} else {
			this.props.setPlayers({ ...this.props.players, data: [], loaded: true, currentOption: { ...this.props.players.currentOption, order: this.props.config.players.options.order[0], direction: this.props.config.players.options.direction[0], type: this.props.config.players.options.type[0] } });
		}

		this.setPlayerProfileScrollLock(this.props.isPlayerProfileModalOpen);
	}

	componentDidUpdate(prevProps) {
		if (prevProps.isPlayerProfileModalOpen !== this.props.isPlayerProfileModalOpen) {
			this.setPlayerProfileScrollLock(this.props.isPlayerProfileModalOpen);
		}
	}

	componentWillUnmount() {
		this.setPlayerProfileScrollLock(false);
		this.props.setPlayers({ ...this.props.players, currentSearch: "", currentSearchType: "uid", data: [] });
	}

	loadPlayers(preLoad) {
		if (!isLoading) {
			isLoading = true;

			if (typeof preLoad === "undefined" || !preLoad) this.props.setPlayers({ ...this.props.players, data: [], loaded: false });

			const params = {
				order: this.props.players.currentOrder,
				searchType: this.props.players.currentSearchType,
				direction: this.props.players.currentDirection,
			};

			if (this.props.players.currentSearch.length) params.search = this.props.players.currentSearch;

			this.props.Interface.request("get", { class: "users", module: "data", method: "list" }, { ...params })
				.then((list) => {
					if (this.props.user.showColumn.includes('deposits') || this.props.user.showColumn.includes('withdrawals') || this.props.user.level >= 6)
						list.forEach((player) => {
							let score = 0;


							if (typeof player.withdrawals !== "undefined") {
								player.withdrawalsTotal = player.withdrawals.total;
								player.withdrawalsCount = player.withdrawals.count;
							} else {
								player.withdrawalsTotal = 0;
								player.withdrawalsCount = 0;
							}

							if (typeof player.deposits !== "undefined") {
								player.depositsTotal = player.deposits.total;
								player.depositsCount = player.deposits.count;
							} else {
								player.depositsTotal = 0;
								player.depositsCount = 0;
							}

							player.profit = player.depositsTotal - player.withdrawalsTotal;

							score = player.depositsTotal - player.withdrawalsTotal;

							if (player.depositsCount) {
								score *= player.depositsCount;
								score *= player.depositsTotal / player.depositsCount / 100;
							}

							if (player.withdrawalsCount) {
								score /= player.withdrawalsCount;
								score /= player.withdrawalsTotal / player.withdrawalsCount / 100;
							}

							score = score / 100;

							player.score = parseFloat(score.toFixed(4));

							delete player.deposits;
							delete player.withdrawals;
						});


					this.props.setPlayers({
						...this.props.players,
						loaded: true,
						error: {
							text: "",
							code: 0,
							timestamp: 0,
						},
						currentSearch: this.currentSearch,
						data: list,
						pagination: { current: 1, total: Math.ceil(list.length / this.props.config.players.perPage) },
					});
					isLoading = false;
				})
				.catch((e) => {
					if (typeof e.text === "undefined") e = { text: e.toString() };
					this.props.setPlayers({ ...this.props.players, loaded: true, error: e });
					isLoading = false;
				});
		}
	}

	compare(a, b) {
		if (a[this.props.players.sorting.sortingColumn.sortingField] < b[this.props.players.sorting.sortingColumn.sortingField]) {
			return this.props.players.sorting.isDescending ? 1 : -1;
		}

		if (a[this.props.players.sorting.sortingColumn.sortingField] > b[this.props.players.sorting.sortingColumn.sortingField]) {
			return this.props.players.sorting.isDescending ? -1 : 1;
		}

		return 0;
	}

	handleKeyPress = (event) => {
		if (event.detail.key === "Enter") {
			if ((this.props.user.level >= 6 || this.props.user.showColumn.includes("allPlayerPermission")) ? false : !this.props.players.currentSearch.length) {
				return;
			}

			setTimeout(() => {
				this.loadPlayers();
			}, 0);
		}
	};

	getEconomicScoreColor(score) {
		return score < 0 ? "red" : score < 5 ? "orange" : score < 50 ? "purple" : "green";
	}

	getVisibleColumns() {
		let columns = [];
		columns.push("uid");
		columns.push("balance");
		columns.push("balance_r");
		columns.push("balance_rakeback");
		columns.push("partner");
		columns.push("date");
		columns.push("casinoID");


		if (this.props.user.showColumn.includes('deposits')) {
			columns.push("deposits");
		}

		if (this.props.user.showColumn.includes('withdrawals')) {
			columns.push("withdrawals");
		}

		if (this.props.user.level >= 6 && this.props.players.currentOption.order.type !== "verify") {
			if (!columns.includes('deposit')) {
				columns.push("deposits");
				columns.push("withdrawals");
				columns.push("profit");
				columns.push("escore");
			}
		}

		if (this.props.players.currentOption.order.type === "verify") {
			columns.push("verifyPendingFrom");
		}

		return columns;
	}

	saveData(data, format = "json") {
		const element = document.createElement("a");
		const fileName = this.props.config.app.name.toLowerCase() + "_players_" + +new Date() + "." + format;
		let file;

		if (format === "csv") {
			file = new Blob([getCsvContent(data)], { type: "text/csv;charset=utf-8;" });
		} else if (format === "xlsx") {
			file = getXlsxBlob(data);
		} else {
			file = new Blob([JSON.stringify(data)], { type: "application/json;charset=utf-8;" });
		}

		element.href = URL.createObjectURL(file);
		element.download = fileName;
		document.body.appendChild(element);
		element.click();
		document.body.removeChild(element);
		URL.revokeObjectURL(element.href);
	}

	checkItem(id) {
		this.props.setPlayers({ ...this.props.players, lastChecked: id });
	}

	render() {
		return (
			<>
				<Modal
					visible={this.state.exportModalVisible}
					onDismiss={() => this.setState({ exportModalVisible: false })}
					header="Export players"
					footer={
						<Box float="right">
							<SpaceBetween direction="horizontal" size="xs">
								<Button variant="link" onClick={() => this.setState({ exportModalVisible: false })}>
									Cancel
								</Button>
								<Button
									variant="primary"
									onClick={() => {
										this.saveData(this.props.players.data, this.state.exportFormat);
										this.setState({ exportModalVisible: false });
									}}
									disabled={!this.props.players.loaded || !this.props.players.data.length}
								>
									Export
								</Button>
							</SpaceBetween>
						</Box>
					}
				>
					<FormField label="Export format">
						<Select
							selectedOption={getExportOption(this.state.exportFormat)}
							options={EXPORT_FORMAT_OPTIONS}
							onChange={(option) => this.setState({ exportFormat: option.detail.selectedOption.value })}
						/>
					</FormField>
				</Modal>
				{this.props.players.loaded && this.props.players.error.text.length ? (
					<Box
						margin={{ bottom: "m" }}
						children={
							<Alert
								type="error"
								header="Error loading data:"
								children={
									<>
										<Box variant="p" children={this.props.players.error.text} />
										<hr />
										<Box
											variant="p"
											children={
												<>
													request error code: {this.props.players.error.code} | request timestamp: {this.props.players.error.timestamp}
												</>
											}
										/>
									</>
								}
							/>
						}
					/>
				) : (
					<></>
				)}

				<Table
					visibleColumns={this.getVisibleColumns()}
					loading={!this.props.players.loaded}
					sortingColumn={{ sortingField: this.props.players.sorting.sortingColumn.sortingField }}
					sortingDescending={this.props.players.sorting.isDescending}
					onSortingChange={(sorting) => {
						this.props.setPlayers({ ...this.props.players, sorting: sorting.detail });
						setTimeout(() => {
							this.props.setPlayers({
								...this.props.players,
								data: cloneDeep(this.props.players.data).sort((a, b) => {
									return this.compare(a, b);
								}),
							});
						}, 0);
					}}
					columnDefinitions={[
						{
							id: "uid",
							header: "UID",
							sortingField: "id",
							cell: (item) => (
								<Button
									iconName={typeof item.verify !== "undefined" && item.verify.status === 3 ? "status-stopped" : typeof item.verify !== "undefined" && item.verify.verified ? "status-positive" : "status-in-progress"}
									className={[this.props.styles["btnUID"], this.props.players.lastChecked === item.id ? this.props.styles["lastCheckedItem"] : "", typeof document.body.classList[0] !== "undefined" ? this.props.styles["dark"] : "", typeof item.verify !== "undefined" && item.verify.verified ? this.props.styles["green"] : typeof item.verify !== "undefined" && item.verify.need ? this.props.styles["red"] : this.props.styles["blue"]].join(" ")}
									variant="normal"
									onClick={() => {
										this.checkItem(item.id);
										this.props.Interface.loadInfo(item.id);
									}}
									children={item.id}
								/>
							),
						},
						{ id: "casinoID", header: "CasinoID", cell: (item) => item.casinoID || "none" },
						{
							id: "deposits",
							header: "Deposits",
							sortingField: "depositsTotal",
							cell: (item) => (
								<Link
									variant="normal"
									onFollow={() => {
										this.checkItem(item.id);
										this.props.Interface.loadPayments(item.id, 1);
									}}
									children={
										<>
											{item.depositsTotal === 0 ? item.depositsTotal : new Intl.NumberFormat("ru-RU").format(item.depositsTotal)} RUB. ({item.depositsCount})
										</>
									}
								/>
							),
						},
						{
							id: "withdrawals",
							header: "Withdrawals",
							sortingField: "withdrawalsTotal",
							cell: (item) => (
								<Link
									variant="normal"
									onFollow={() => {
										this.checkItem(item.id);
										this.props.Interface.loadPayments(item.id, 2);
									}}
									children={
										<>
											{new Intl.NumberFormat("ru-RU").format(item.withdrawalsTotal)} RUB. ({item.withdrawalsCount})
										</>
									}
								/>
							),
						},
						{ id: "profit", header: "Profit", sortingField: "profit", cell: (item) => <>{new Intl.NumberFormat("ru-RU").format(item.profit)} RUB.</> },
						{
							id: "escore",
							header: "EScore",
							sortingField: "score",
							cell: (item) => <Badge className={[this.props.styles["badge"], this.props.styles[this.getEconomicScoreColor(item.score)]].join(" ")} children={item.score} />,
						},
						{
							id: "player",
							header: "Player",
							sortingField: "settings.name",
							cell: (item) => (
								<>
									{" "}
									<Avatar vkontakteId="1" size="150" />
									{item.settings.name}
								</>
							),
						},
						{ id: "balance", header: "Balance", sortingField: "balance", cell: (item) => <>{new Intl.NumberFormat("ru-RU").format(item.balance)} RUB.</> },
						{ id: "balance_rakeback", header: "Rakeback balance", sortingField: "balance_rakeback", cell: (item) => <>{new Intl.NumberFormat("ru-RU").format(item.balance_rakeback)} RUB.</> },
						{ id: "balance_r", header: "Partner balance", sortingField: "balance_r", cell: (item) => <>{new Intl.NumberFormat("ru-RU").format(item.balance_r)} RUB.</> },
						{
							id: "partner",
							header: "Partner",
							cell: (item) =>
								item.referer ? (
									<Link
										variant="normal"
										onFollow={() => {
											this.checkItem(item.id);
											this.props.Interface.loadInfo(item.referer);
										}}
										children={item.referer}
									/>
								) : (
									"none"
								),
						},
						{ id: "verifyPendingFrom", header: "Pending from", sortingField: "verifyPendingFrom", cell: (item) => (typeof item.verifyPendingFrom !== "undefined" ? new Date(item.verifyPendingFrom).toLocaleDateString() : <></>) },
						{ id: "date", header: "Registration", sortingField: "date", cell: (item) => new Date(item.date).toLocaleDateString() },
					]}
					items={this.props.players.data.slice((this.props.players.pagination.current - 1) * this.props.config.players.perPage, this.props.players.pagination.current * this.props.config.players.perPage)}
					pagination={
						<Pagination
							disabled={!this.props.players.loaded}
							currentPageIndex={this.props.players.pagination.current}
							pagesCount={this.props.players.pagination.total}
							onChange={(page) => {
								this.props.setPlayers({ ...this.props.players, pagination: { ...this.props.players.pagination, current: page.detail.currentPageIndex } });
							}}
						/>
					}
					preferences={
						<Preferences
							cancelLabel="Cancel"
							confirmLabel="Confirm"
							pageSizePreference={{ title: "Logs per page", options: this.props.config.players.preferences.perPage }}
							preferences={{ pageSize: this.props.config.players.perPage }}
							title="Preferences"
							onConfirm={(preferences) => {
								localStorage.setItem("players.perPage", preferences.detail.pageSize);
								this.props.setConfig({ ...this.props.config, players: { ...this.props.config.players, perPage: preferences.detail.pageSize } });
								this.props.setPlayers({ ...this.props.players, pagination: { current: 1, total: Math.ceil(this.props.players.data.length / preferences.detail.pageSize) } });
							}}
						/>
					}
					filter={
						<Box
							margin={{ bottom: "m" }}
							children={
								<Grid
									gridDefinition={[{ colspan: 3 }, { colspan: 3 }, { colspan: 3 }, { colspan: 3 }]}
									children={
										<>
											<Select
												filteringType="auto"
												disabled={!this.props.players.loaded || this.props.players.currentOption.order.type === "deposits" || this.props.players.currentOption.order.type === "withdrawals" ? true : false}
												placeholder="Direction"
												selectedOption={this.props.players.currentOption.direction}
												onChange={(option) => {
													option.detail.selectedOption.type !== this.props.players.currentDirection && this.props.setPlayers({ ...this.props.players, currentDirection: option.detail.selectedOption.type, currentOption: { ...this.props.players.currentOption, direction: option.detail.selectedOption } });
												}}
												options={this.props.config.players.options.direction}
											/>
											<Select
												filteringType="auto"
												disabled={!this.props.players.loaded}
												placeholder="Order"
												selectedOption={this.props.players.currentOption.order}
												onChange={(option) => {
													option.detail.selectedOption.order !== this.props.players.currentOrder && this.props.setPlayers({ ...this.props.players, currentOrder: option.detail.selectedOption.type, currentOption: { ...this.props.players.currentOption, order: option.detail.selectedOption } });
													option.detail.selectedOption.order !== this.props.players.currentOrder &&
													setTimeout(() => {
														this.loadPlayers();
													}, 0);
												}}
												options={this.props.config.players.options.order}
											/>
											<Select
												filteringType="auto"
												disabled={!this.props.players.loaded}
												placeholder="Type"
												selectedOption={this.props.players.currentOption.type}
												onChange={(option) => {
													option.detail.selectedOption.type !== this.props.players.currentSearchType && this.props.setPlayers({ ...this.props.players, currentSearchType: option.detail.selectedOption.type, currentOption: { ...this.props.players.currentOption, type: option.detail.selectedOption } });
												}}
												options={this.props.config.players.options.type}
											/>
											<Input
												disabled={!this.props.players.loaded}
												onKeyDown={this.handleKeyPress}
												disableBrowserAutocorrect={true}
												placeholder="UID, name or something"
												inputMode="search"
												type="search"
												value={this.props.players.currentSearch}
												onChange={(input) => {
													this.props.setPlayers({ ...this.props.players, currentSearch: input.detail.value });
													this.currentSearch = input.detail.value;
												}}
											/>
										</>
									}
								/>
							}
						/>
					}
					header={
						<Box
							margin={{ bottom: "m" }}
							children={
								<Header
									children="Players"
									counter={<>({this.props.players.data.length})</>}
									actions={
										<SpaceBetween size="l" direction="horizontal">
											<Button
												disabled={!this.props.players.loaded || !this.props.players.data.length}
												className={this.props.styles["players-search-button"]}
												onClick={() => {
													this.setState({ exportModalVisible: true });
												}}
												variant="primary"
												children="Export"
											/>
											<Button
												disabled={
													!this.props.players.loaded ||
													(
													  !(this.props.user.level >= 6 || this.props.user.showColumn.includes("allPlayerPermission")) &&
													  !this.props.players.currentSearch.length
													)
												  }
												className={this.props.styles["players-search-button"]}
												onClick={() => {
													setTimeout(() => {
														this.loadPlayers();
													}, 0);
												}}
												variant="primary"
												children="Find"
											/>
										</SpaceBetween>
									}
									description="Below listed up to 500 matched players"
								/>
							}
						/>
					}
					empty="No players found with requested filter"
				/>
			</>
		);
	}
}

function withRecoil(Component) {
	return function WrappedComponent(props) {
		const players = useRecoilValue(playerStore);
		const setPlayers = useSetRecoilState(playerStore);
		const config = useRecoilValue(configStore);
		const setConfig = useSetRecoilState(configStore);
		const user = useRecoilValue(userStore);
		const isPlayerProfileModalOpen = useRecoilValue(playerPageStore).show;

		return <Component {...props} players={players} setPlayers={setPlayers} config={config} setConfig={setConfig} user={user} isPlayerProfileModalOpen={isPlayerProfileModalOpen} />;
	};
}

export default withRecoil(Players);
